import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../auth/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Countdown from "../components/Countdown";
import CompetitionRibbon from "../components/CompetitionRibbon";
import { api } from "../lib/api";
import { formatMondayDate } from "../lib/date";
import { ArrowRight, Check, Sparkles, Calendar, Clock, BookOpen, Brain } from "lucide-react";

const COAT = "https://customer-assets.emergentagent.com/job_66cda0d4-d07a-4a92-8bdd-b78d3aba21d7/artifacts/xbhowqoo_ephigie-hayti.jpg";
const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/66cda0d4-d07a-4a92-8bdd-b78d3aba21d7/images/04ba5c0cce97062acb02405bdca169e702817f62a4fbc16b5f1f30bdc2fea7f4.png";
const STUDENTS = "https://static.prod-images.emergentagent.com/jobs/66cda0d4-d07a-4a92-8bdd-b78d3aba21d7/images/42eb0fef54a7af0eb8d34d7b0078611a3d344641932b09f15b8975afa138e100.png";

export default function CohortInfo() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [syllabus, setSyllabus] = useState([]);

  // Smooth-scroll to the hash anchor when arriving from Header / Footer cross-route links.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace("#", "");
    // Wait for the DOM to settle (fonts/images can shift layout).
    const t1 = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => clearTimeout(t1);
  }, [location.hash]);


  useEffect(() => {
    // try fetching public-ish syllabus; if 401, just use static fallback
    api.get("/assignments").then((r) => setSyllabus(r.data)).catch(() => setSyllabus([]));
  }, []);

  const fallbackSyllabus = [
    { week: 1, live_date: "2026-06-15", title: "Entwodiksyon & Domèn", titles: { en: "Introduction & Domain", fr: "Introduction & Domaine", ht: "Entwodiksyon & Domèn" } },
    { week: 2, live_date: "2026-06-22", title: "Ebègeman", titles: { en: "Hosting Management", fr: "Hébergement", ht: "Ebègeman" } },
    { week: 3, live_date: "2026-06-29", title: "Cpanel", titles: { en: "Cpanel", fr: "Cpanel", ht: "Cpanel" } },
    { week: 4, live_date: "2026-07-06", title: "WordPress", titles: { en: "WordPress", fr: "WordPress", ht: "WordPress" } },
    { week: 5, live_date: "2026-07-13", title: "Divi Builder", titles: { en: "Divi Builder", fr: "Divi Builder", ht: "Divi Builder" } },
    { week: 6, live_date: "2026-07-20", title: "Kreyasyon Pratik", titles: { en: "Practical Creation", fr: "Création Pratique", ht: "Kreyasyon Pratik" } },
    { week: 7, live_date: "2026-07-27", title: "Kontni & Lyen", titles: { en: "Content & Links", fr: "Contenu & Liens", ht: "Kontni & Lyen" } },
    { week: 8, live_date: "2026-08-03", title: "Zouti Multimedia", titles: { en: "Multimedia Tools", fr: "Outils Multimedia", ht: "Zouti Multimedia" } },
    { week: 9, live_date: "2026-08-10", title: "Extansyon", titles: { en: "Extensions", fr: "Extensions", ht: "Extansyon" } },
    { week: 10, live_date: "2026-08-17", title: "Administrasyon", titles: { en: "Administration", fr: "Administration", ht: "Administrasyon" } },
    { week: 11, live_date: "2026-08-24", title: "Maketing", titles: { en: "Marketing", fr: "Marketing", ht: "Maketing" } },
    { week: 12, live_date: "2026-08-31", title: "Legal", titles: { en: "Legal", fr: "Legal", ht: "Legal" } },
  ];
  const weeks = (syllabus.length ? syllabus : fallbackSyllabus).slice(0, 12);

  const goEnroll = (tier) => {
    if (!user) {
      nav(`/register?tier=${tier}`);
    } else {
      nav(`/enroll?tier=${tier}`);
    }
  };

  return (
    <div data-testid="cohort-info-page">
      <CompetitionRibbon />
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#141414]">
        <div
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black via-black/80 to-black/40" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-32 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <div className="overline mb-5" data-testid="hero-overline">{t("hero.overline")}</div>
            <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight">
              <span className="block">{t("hero.title")}</span>
            </h1>
            <div className="mt-4 font-condensed tracking-[0.18em] uppercase text-[13px] text-[#d4af37]" data-testid="hero-tagline">
              {t("hero.tagline")}
            </div>
            <p className="mt-5 max-w-xl text-neutral-300 text-base md:text-lg leading-relaxed">
              {t("hero.subtitle")}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button
                data-testid="hero-register-btn"
                onClick={() => goEnroll("standard")}
                className="btn-imperial"
              >
                {t("hero.register")} <ArrowRight size={16} />
              </button>
              <Link
                to="/enroll-advanced"
                data-testid="hero-advanced-btn"
                className="btn-outline-gold inline-flex items-center gap-2"
              >
                <Brain size={16} /> {t("nav.advancedCohort")}
              </Link>
              <a href="#syllabus" data-testid="hero-syllabus-btn" className="btn-outline-gold">
                {t("hero.learnMore")}
              </a>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-neutral-400 border-t border-[#1a1a1a] pt-6">
              <div>
                <div className="overline text-[9px]">{t("hero.cohort")}</div>
                <div className="font-condensed text-lg text-[#d4af37] mt-1">June 15, 2026 · 7:30 PM</div>
              </div>
              <div className="h-10 w-px bg-[#222]" />
              <div>
                <div className="overline text-[9px]">{t("hero.duration")}</div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="lg:col-span-5 flex justify-center"
          >
            <div className="relative">
              <div className="absolute -inset-10 bg-[#d4af37]/10 blur-3xl rounded-full" />
              <img src={COAT} alt="Haitian Imperial coat of arms" className="relative h-72 md:h-96 object-contain mix-blend-screen" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* COUNTDOWN STRIP */}
      <section className="border-b border-[#141414] py-14 md:py-20" data-testid="countdown-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10 flex justify-center">
          <Countdown />
        </div>
      </section>

      {/* SYLLABUS */}
      <section id="syllabus" className="py-24 md:py-32 border-b border-[#141414]">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl mb-10">
            <div className="overline mb-4">12 SUBJECTS · 12 MONDAYS</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">{t("syllabus.title")}</h2>
            <p className="mt-4 text-neutral-400">{t("syllabus.subtitle")}</p>
            {(() => {
              const fb = t("syllabus.feedback", "");
              if (!fb || fb === "syllabus.feedback") return null;
              return (
                <div className="mt-3 inline-flex items-center gap-2 text-[12px] font-condensed tracking-widest uppercase text-[#d4af37]" data-testid="syllabus-feedback-bullet">
                  <span>✓</span> {fb}
                </div>
              );
            })()}
          </div>

          {/* Format strip */}
          <div className="grid sm:grid-cols-3 gap-3 mb-10" data-testid="format-strip">
            <div className="card-imperial p-4 flex items-center gap-3">
              <Calendar size={18} className="text-[#d4af37]" />
              <div>
                <div className="overline text-[10px]">{t("format.liveLabel")}</div>
                <div className="text-sm">{t("format.liveDay")}</div>
              </div>
            </div>
            <div className="card-imperial p-4 flex items-center gap-3">
              <Clock size={18} className="text-[#d4af37]" />
              <div>
                <div className="overline text-[10px]">{t("format.durationLabel")}</div>
                <div className="text-sm">1 {t("format.hour")} · 7:30 PM</div>
              </div>
            </div>
            <div className="card-imperial p-4 flex items-center gap-3">
              <BookOpen size={18} className="text-[#d4af37]" />
              <div>
                <div className="overline text-[10px]">{t("format.practiceLabel")}</div>
                <div className="text-sm">{t("format.practice")}</div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="syllabus-grid">
            {weeks.map((w, i) => {
              const itemKey = `syllabus.item_${w.week}`;
              const overrideTitle = t(itemKey, "__MISS__");
              const localizedTitle = overrideTitle !== "__MISS__" && overrideTitle !== itemKey
                ? overrideTitle
                : ((w.titles && w.titles[lang]) || w.title);
              const localizedDesc = (w.descriptions && w.descriptions[lang]) || w.description;
              const dateLabel = w.live_date ? formatMondayDate(w.live_date, lang) : null;
              return (
                <motion.div
                  key={w.week}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.04 }}
                  className="card-imperial p-5 group"
                  data-testid={`syllabus-week-${w.week}`}
                >
                  <div className="font-serif text-xl leading-tight group-hover:text-[#d4af37] transition-colors">{localizedTitle}</div>
                  {localizedDesc && <div className="text-xs text-neutral-500 mt-2 line-clamp-3">{localizedDesc}</div>}
                  <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] text-[#d4af37]/80 font-condensed tracking-widest uppercase" data-testid={`syllabus-date-${w.week}`}>
                    <Calendar size={11} /> {t("format.liveDay")} · 7:30 PM
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* COHORT COMPARE — public, side-by-side */}
      <section id="cohorts" className="py-24 md:py-28 border-b border-[#141414] relative" data-testid="cohort-compare">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="max-w-3xl mb-12">
            <div className="overline mb-4" data-testid="cohort-compare-overline">{t("cohortCompare.overline")}</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight" data-testid="cohort-compare-title">{t("cohortCompare.title")}</h2>
            <p className="mt-4 text-neutral-400" data-testid="cohort-compare-subtitle">{t("cohortCompare.subtitle")}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Standard */}
            <Link
              to="/enroll?tier=standard"
              data-testid="cohort-compare-standard"
              className="group card-imperial p-8 md:p-9 transition-all hover:border-[#d4af37]/60 hover:-translate-y-0.5 block"
            >
              <div className="flex items-center gap-2 mb-3">
                <Calendar size={16} className="text-[#d4af37]" />
                <div className="overline text-[#d4af37]">MONDAY · WEB PRO</div>
              </div>
              <h3 className="font-serif text-3xl md:text-4xl">{t("cohortCompare.standardName")}</h3>
              <div className="mt-2 text-neutral-400 text-sm">{t("cohortCompare.standardSub")}</div>
              <div className="mt-5 flex items-baseline gap-2">
                <span className="font-serif text-5xl">$499</span>
                <span className="text-neutral-500 text-sm">USD · 12 weeks</span>
              </div>
              <div className="mt-2 text-xs font-condensed tracking-[0.14em] uppercase text-[#d4af37]/80">{t("cohortCompare.standardStart")}</div>
              <ul className="mt-6 space-y-2.5">
                {(Array.isArray(t("cohortCompare.standardBullets")) ? t("cohortCompare.standardBullets") : []).map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-neutral-300">
                    <Check size={16} className="text-[#d4af37] mt-0.5 flex-shrink-0" /> {b}
                  </li>
                ))}
              </ul>
              <div className="mt-7 inline-flex items-center gap-1.5 text-sm font-condensed tracking-widest uppercase text-[#d4af37] group-hover:text-white transition-colors">
                {t("cohortCompare.viewDetails")} <ArrowRight size={14} />
              </div>
            </Link>

            {/* Advanced */}
            <Link
              to="/enroll-advanced"
              data-testid="cohort-compare-advanced"
              className="group relative card-imperial p-8 md:p-9 transition-all hover:border-[#e6192b]/60 hover:-translate-y-0.5 block gold-shimmer-border"
            >
              <div className="absolute -top-3 left-8 bg-[#e6192b] text-white px-3 py-1 text-xs font-condensed tracking-widest uppercase flex items-center gap-1">
                <Sparkles size={12} /> NEW · AI
              </div>
              <div className="flex items-center gap-2 mb-3">
                <Brain size={16} className="text-[#e6192b]" />
                <div className="overline text-[#e6192b]">THURSDAY · AI-DRIVEN</div>
              </div>
              <h3 className="font-serif text-3xl md:text-4xl">{t("cohortCompare.advancedName")}</h3>
              <div className="mt-2 text-neutral-400 text-sm">{t("cohortCompare.advancedSub")}</div>
              <div className="mt-5 flex items-baseline gap-2">
                <span className="font-serif text-5xl">$750</span>
                <span className="text-neutral-500 text-sm">USD · 12 weeks</span>
              </div>
              <div className="mt-2 text-xs font-condensed tracking-[0.14em] uppercase text-[#e6192b]/90">{t("cohortCompare.advancedStart")}</div>
              <ul className="mt-6 space-y-2.5">
                {(Array.isArray(t("cohortCompare.advancedBullets")) ? t("cohortCompare.advancedBullets") : []).map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-neutral-300">
                    <Check size={16} className="text-[#e6192b] mt-0.5 flex-shrink-0" /> {b}
                  </li>
                ))}
              </ul>
              <div className="mt-7 inline-flex items-center gap-1.5 text-sm font-condensed tracking-widest uppercase text-[#e6192b] group-hover:text-white transition-colors">
                {t("cohortCompare.viewDetails")} <ArrowRight size={14} />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-24 md:py-32 border-b border-[#141414] relative">
        <div
          className="absolute inset-0 opacity-20"
          style={{ backgroundImage: `url(${STUDENTS})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/95 to-black" />
        <div className="relative max-w-6xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl mb-14">
            <div className="overline mb-4">{t("pricing.title")}</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">{t("pricing.subtitle")}</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <PricingCard
              tier="standard"
              label={t("pricing.standard")}
              price={499}
              perCohort={t("pricing.perCohort")}
              features={t("pricing.features")}
              includes={t("pricing.includes")}
              cta={t("pricing.cta")}
              onClick={() => goEnroll("standard")}
              testId="pricing-standard"
            />
            <PricingCard
              tier="member"
              label={t("pricing.member")}
              price={399}
              perCohort={t("pricing.perCohort")}
              features={t("pricing.features")}
              includes={t("pricing.includes")}
              cta={t("pricing.cta")}
              onClick={() => goEnroll("member")}
              highlight
              badge={t("pricing.memberBadge")}
              testId="pricing-member"
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function PricingCard({ label, price, perCohort, features, includes, cta, onClick, highlight, badge, testId }) {
  return (
    <div
      data-testid={testId}
      className={`relative card-imperial p-8 md:p-10 ${highlight ? "gold-shimmer-border border-[#d4af37]/40" : ""}`}
    >
      {highlight && badge && (
        <div className="absolute -top-3 left-8 bg-[#d4af37] text-black px-3 py-1 text-xs font-condensed tracking-widest uppercase flex items-center gap-1">
          <Sparkles size={12} /> {badge}
        </div>
      )}
      <div className="overline mb-4">{label}</div>
      <div className="flex items-baseline gap-2">
        <span className="font-serif text-6xl md:text-7xl">${price}</span>
        <span className="text-neutral-500 text-sm">{perCohort}</span>
      </div>
      <div className="mt-6 mb-2 overline">{includes}</div>
      <ul className="space-y-2.5">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-neutral-300">
            <Check size={16} className="text-[#d4af37] mt-0.5 flex-shrink-0" /> {f}
          </li>
        ))}
      </ul>
      <button
        data-testid={`${testId}-cta`}
        onClick={onClick}
        className={`mt-8 w-full ${highlight ? "btn-imperial" : "btn-outline-gold"}`}
      >
        {cta} <ArrowRight size={16} />
      </button>
    </div>
  );
}
